import React, { useState, useEffect, useRef } from 'react';
import {
  Users,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Key,
  Mail,
  Eye,
  UserCheck,
  UserX,
  ChevronLeft,
  ChevronRight,
  Send
} from 'lucide-react';
import { CardLoadingSpinner } from '../ui/LoadingSpinner';
import { superAdminAPI } from '../../services/api';
import AdminModal from './AdminModal';

const AdminManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTenant, setSelectedTenant] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(0); // 0-based for consistency with questions
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add', 'edit', 'view'
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [selectedAdmins, setSelectedAdmins] = useState(new Set());
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);

  // Refs for table scrolling
  const tableScrollRef = useRef(null);
  const tableRef = useRef(null);

  // Load admins on component mount and when pagination changes
  useEffect(() => {
    fetchAdmins();
  }, [currentPage, pageSize]);

  // Load tenants on component mount
  useEffect(() => {
    fetchTenants();
  }, []);

  // Reload admins when filters change (with debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(0); // Reset to first page when filtering
      fetchAdmins();
    }, 300); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedTenant, selectedStatus]);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        size: pageSize,
        sortBy: 'createdAt',
        sortDir: 'desc'
      };

      // Add filters
      if (searchTerm) params.search = searchTerm;
      if (selectedTenant) params.tenantId = selectedTenant;
      if (selectedStatus) params.status = selectedStatus;
      params.roles = 'ROLE_ADMIN'; // Only fetch admin users
      params.excludeRoles = 'ROLE_SUPER_ADMIN'; // Exclude super admins

      const response = await superAdminAPI.searchUsers(params);
      const data = response.data;

      setAdmins(data.content || []);
      setTotalElements(data.totalElements || 0);
      setTotalPages(data.totalPages || 0);
    } catch (error) {
      console.error('Error fetching admins:', error);
      setAdmins([]);
      setTotalElements(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await superAdminAPI.getAllTenants({ size: 1000 });
      setTenants(response.data.content || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  const handleAddAdmin = () => {
    setSelectedAdmin(null);
    setModalMode('add');
    setShowModal(true);
  };

  const handleEditAdmin = (admin) => {
    setSelectedAdmin(admin);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleViewAdmin = (admin) => {
    setSelectedAdmin(admin);
    setModalMode('view');
    setShowModal(true);
  };

  const handleResetPassword = async (admin) => {
    if (window.confirm(`Reset password for ${admin.name}? A new temporary password will be generated and sent via email.`)) {
      try {
        await superAdminAPI.resetUserPassword(admin.id, { sendEmail: true });
        alert('Password reset successfully. New temporary password sent via email.');
      } catch (error) {
        console.error('Error resetting password:', error);
        alert('Failed to reset password. Please try again.');
      }
    }
  };

  const handleToggleStatus = async (admin) => {
    const newStatus = !admin.enabled;
    const action = newStatus ? 'activate' : 'deactivate';

    if (window.confirm(`Are you sure you want to ${action} ${admin.name}?`)) {
      try {
        await superAdminAPI.updateUserStatus(admin.id, { active: newStatus });
        fetchAdmins(); // Refresh the list
      } catch (error) {
        console.error('Error updating admin status:', error);
        alert(`Failed to ${action} admin. Please try again.`);
      }
    }
  };

  const handleDeleteAdmin = async (admin) => {
    if (window.confirm(`Are you sure you want to delete ${admin.name}? This action cannot be undone.`)) {
      try {
        await superAdminAPI.deleteUser(admin.id);
        fetchAdmins(); // Refresh the list
      } catch (error) {
        console.error('Error deleting admin:', error);
        alert('Failed to delete admin. Please try again.');
      }
    }
  };

  const handleResendWelcomeEmail = async (admin) => {
    if (!window.confirm(`Are you sure you want to resend the welcome email to ${admin.name}?\n\nThis will generate a new temporary password and send it to ${admin.email}.`)) {
      return;
    }

    try {
      const tenantName = admin.tenantId ? getTenantName(admin.tenantId) : 'Your Institution';

      await superAdminAPI.resendAdminWelcomeEmail(admin.id, {
        tenantName: tenantName
      });

      alert(`Welcome email resent successfully to ${admin.email}!\n\nA new temporary password has been generated and sent.`);
      fetchAdmins(); // Refresh the admin list to update any status changes
    } catch (error) {
      console.error('Error resending welcome email:', error);
      const errorMessage = error.response?.data?.message || 'Failed to resend welcome email. Please try again.';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleModalSave = (shouldCloseModal = false) => {
    fetchAdmins(); // Refresh the list
    if (shouldCloseModal) {
      setShowModal(false);
    }
  };

  const getTenantName = (tenantId) => {
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant ? tenant.name : 'Unknown Tenant';
  };

  // Bulk selection handlers
  const handleSelectAdmin = (adminId) => {
    const newSelected = new Set(selectedAdmins);
    if (newSelected.has(adminId)) {
      newSelected.delete(adminId);
    } else {
      newSelected.add(adminId);
    }
    setSelectedAdmins(newSelected);
    setIsSelectAllChecked(newSelected.size === admins.length && admins.length > 0);
  };

  const handleSelectAll = () => {
    if (isSelectAllChecked) {
      setSelectedAdmins(new Set());
      setIsSelectAllChecked(false);
    } else {
      const allAdminIds = new Set(admins.map(admin => admin.id));
      setSelectedAdmins(allAdminIds);
      setIsSelectAllChecked(true);
    }
  };

  const handleBulkDelete = () => {
    if (selectedAdmins.size === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedAdmins.size} selected admin(s)? This action cannot be undone.`)) {
      confirmBulkDelete();
    }
  };

  const confirmBulkDelete = async () => {
    try {
      const adminIds = Array.from(selectedAdmins);

      // Delete each admin individually (since we don't have bulk delete API)
      for (const adminId of adminIds) {
        await superAdminAPI.deleteUser(adminId);
      }

      setSelectedAdmins(new Set());
      setIsSelectAllChecked(false);
      fetchAdmins(); // Refresh the list
      alert(`Successfully deleted ${adminIds.length} admin(s)!`);
    } catch (error) {
      console.error('Error deleting admins:', error);
      alert('Failed to delete some admins. Please try again.');
    }
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0); // Reset to first page
  };



  const filteredAdmins = admins.filter(admin =>
    admin.roles && admin.roles.includes('ROLE_ADMIN')
  );

  return (
    <div>
      <div className="page-header-compact">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold azure-text mb-1">Admin Management</h1>
            <p className="text-sm text-blue-700 font-medium">
              Manage tenant administrators and their access
            </p>
          </div>
          <div className="flex space-x-3">
            {selectedAdmins.size > 0 && (
              <button
                onClick={handleBulkDelete}
                className="btn-danger flex items-center"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedAdmins.size})
              </button>
            )}
            <button
              onClick={handleAddAdmin}
              className="btn-primary flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Admin
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card mb-4 py-3 px-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search admins..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <select
            className="input-field w-auto"
            value={selectedTenant}
            onChange={(e) => setSelectedTenant(e.target.value)}
          >
            <option value="">All Tenants</option>
            {tenants.map(tenant => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name} ({tenant.code})
              </option>
            ))}
          </select>
          <select
            className="input-field w-auto"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedTenant('');
              setSelectedStatus('');
              setCurrentPage(0);
            }}
            className="btn-secondary"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Admins Table */}
      <div className="card">
        {loading ? (
          <CardLoadingSpinner message="Loading admins..." />
        ) : (
          <div>
            {/* Table Container */}
            <div
              ref={tableScrollRef}
              className="table-container-fixed"
            >
              <div ref={tableRef}>
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="table-header w-10 sticky left-0 bg-slate-50 shadow-lg z-10">
                        <input
                          type="checkbox"
                          checked={isSelectAllChecked}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="table-header w-32 sticky left-10 bg-slate-50 shadow-lg z-10">Actions</th>
                      <th className="table-header">Name</th>
                      <th className="table-header">Email</th>
                      <th className="table-header hidden md:table-cell">Tenant</th>
                      <th className="table-header hidden lg:table-cell">Status</th>
                      <th className="table-header hidden lg:table-cell">First Login</th>
                      <th className="table-header hidden xl:table-cell">Last Login</th>
                      <th className="table-header hidden xl:table-cell">Created</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {filteredAdmins.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="table-cell text-center py-12">
                          <div className="text-slate-500">
                            {admins.length === 0 ? 'No admins found. Add some admins to get started.' : 'No admins match your search criteria.'}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredAdmins.map((admin) => {
                        const isSuperAdmin = admin.roles && admin.roles.includes('ROLE_SUPER_ADMIN');
                        return (
                        <tr key={admin.id} className={`hover:bg-slate-50 ${isSuperAdmin ? 'bg-blue-50' : ''}`}>
                          <td className={`table-cell sticky left-0 shadow-lg z-10 ${isSuperAdmin ? 'bg-blue-50' : 'bg-white'}`}>
                            <input
                              type="checkbox"
                              checked={selectedAdmins.has(admin.id)}
                              onChange={() => handleSelectAdmin(admin.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              disabled={isSuperAdmin}
                            />
                          </td>
                          <td className={`table-cell sticky left-10 shadow-lg z-10 ${isSuperAdmin ? 'bg-blue-50' : 'bg-white'}`}>
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => handleViewAdmin(admin)}
                                className="text-slate-600 hover:text-slate-900 p-1 hover:bg-slate-100 transition-colors duration-200"
                                title="View Admin"
                              >
                                <Eye className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => handleEditAdmin(admin)}
                                className="text-slate-600 hover:text-slate-900 p-1 hover:bg-slate-100 transition-colors duration-200"
                                title="Edit Admin"
                              >
                                <Edit className="h-3 w-3" />
                              </button>
                              {!isSuperAdmin && (
                                <>
                                  <button
                                    onClick={() => handleResetPassword(admin)}
                                    className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 transition-colors duration-200"
                                    title="Reset Password"
                                  >
                                    <Key className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={() => handleResendWelcomeEmail(admin)}
                                    className="text-purple-600 hover:text-purple-900 p-1 hover:bg-purple-50 transition-colors duration-200"
                                    title="Resend Welcome Email"
                                  >
                                    <Send className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={() => handleToggleStatus(admin)}
                                    className={`p-1 transition-colors duration-200 ${
                                      admin.enabled
                                        ? 'text-orange-600 hover:text-orange-900 hover:bg-orange-50'
                                        : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                                    }`}
                                    title={admin.enabled ? "Deactivate Admin" : "Activate Admin"}
                                  >
                                    {admin.enabled ? <UserX className="h-3 w-3" /> : <UserCheck className="h-3 w-3" />}
                                  </button>
                                  <button
                                    onClick={() => handleDeleteAdmin(admin)}
                                    className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 transition-colors duration-200"
                                    title="Delete Admin"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                          <td className="table-cell font-medium">
                            <div className="flex items-center space-x-2">
                              <span>{admin.name}</span>
                              {isSuperAdmin && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Super Admin
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="table-cell">{admin.email}</td>

                          <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-700 border-b border-slate-100 hidden md:table-cell">
                            {admin.tenantId ? getTenantName(admin.tenantId) : 'No Tenant'}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-700 border-b border-slate-100 hidden lg:table-cell">
                            <span className={`status-badge ${admin.enabled ? 'status-active' : 'status-inactive'}`}>
                              {admin.enabled ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-700 border-b border-slate-100 hidden lg:table-cell">
                            <span className={`status-badge ${admin.firstTimeLogin ? 'status-pending' : 'status-completed'}`}>
                              {admin.firstTimeLogin ? 'Pending' : 'Completed'}
                            </span>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-700 border-b border-slate-100 hidden xl:table-cell">
                            {admin.lastLoginAt ? new Date(admin.lastLoginAt).toLocaleDateString() : 'Never'}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-700 border-b border-slate-100 hidden xl:table-cell">
                            {admin.createdAt ? new Date(admin.createdAt).toLocaleDateString() : 'Unknown'}
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="px-4 sm:px-6 py-4 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                <span className="text-sm text-slate-700 text-center sm:text-left">
                  Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} admins
                </span>
                <div className="flex items-center justify-center sm:justify-start space-x-2">
                  <label className="text-sm text-slate-700">Show:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                    className="input-field py-1 text-sm"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                  className="btn-secondary flex items-center disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Prev</span>
                </button>

                <span className="text-sm text-slate-700">
                  Page {currentPage + 1} of {totalPages}
                </span>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages - 1}
                  className="btn-secondary flex items-center disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">Next</span>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Admin Modal */}
      {showModal && (
        <AdminModal
          isOpen={showModal}
          mode={modalMode}
          admin={selectedAdmin}
          tenants={tenants}
          onClose={() => setShowModal(false)}
          onSave={handleModalSave}
          onResendEmail={handleResendWelcomeEmail}
        />
      )}
    </div>
  );
};

export default AdminManagement;
